import type { CSSProperties, ReactNode } from 'react';
import { Icon, type IconName } from '@serota/icons/web';
import { copy, formatTime } from '@serota/i18n';
import { cx, useOnline } from '../lib';
import { Button } from './Button';

export interface EmptyStateProps {
  icon: IconName;
  title: ReactNode;
  body?: ReactNode;
  tone?: 'neutral' | 'warn' | 'crit';
  action?: ReactNode;
  card?: boolean;
  style?: CSSProperties;
}

/** Marka ikon kutusu + başlık + tek cümle + en fazla bir aksiyon. Karakter illüstrasyonu yok. */
export function EmptyState({ icon, title, body, tone = 'neutral', action, card, style }: EmptyStateProps) {
  return (
    <div className={cx(card && 'sr-card', 'sr-empty')} style={style}>
      <div className={cx('sr-empty__art', tone === 'warn' && 'is-warn', tone === 'crit' && 'is-crit')}>
        <Icon name={icon} />
      </div>
      <h4>{title}</h4>
      {body ? <p>{body}</p> : null}
      {action}
    </div>
  );
}

export function ErrorState({
  onRetry,
  title = copy.states.serverError,
  body = copy.states.serverErrorBody,
  card,
}: {
  onRetry?: () => void;
  title?: string;
  body?: string;
  card?: boolean;
}) {
  return (
    <EmptyState
      card={card}
      icon="uyari"
      tone="crit"
      title={title}
      body={body}
      action={
        onRetry ? (
          <Button size="sm" variant="secondary" icon="refresh" onClick={onRetry}>
            {copy.actions.retry}
          </Button>
        ) : null
      }
    />
  );
}

export function ForbiddenState({ card }: { card?: boolean }) {
  return <EmptyState card={card} icon="guvenlik" title={copy.states.forbidden} body={copy.states.forbiddenBody} />;
}

export function OfflineBadge({ lastSync, pending }: { lastSync?: Date | string | number | null; pending?: number }) {
  return (
    <span className="sr-row" style={{ gap: 12 }}>
      <span className="sr-offline">
        <Icon name="wifi-off" />
        Çevrimdışı{lastSync ? ` · son eşitleme ${formatTime(lastSync)}` : ''}
      </span>
      {pending ? (
        <span className="sr-row caption sr-muted">
          <Icon name="refresh" />
          {pending} kayıt eşitlenmeyi bekliyor
        </span>
      ) : null}
    </span>
  );
}

export function Skeleton({
  width,
  height = 14,
  radius,
  style,
}: {
  width?: number | string;
  height?: number | string;
  radius?: number;
  style?: CSSProperties;
}) {
  return <div className="sr-skel" aria-hidden style={{ width, height, borderRadius: radius, ...style }} />;
}

/** Tablo/liste iskeleti: son düzenin şeklini taklit eder. */
export function SkeletonRows({ rows = 6, columns = 5 }: { rows?: number; columns?: number }) {
  return (
    <div className="sr-stack" style={{ gap: 12, padding: 16 }} aria-busy aria-label="Yükleniyor">
      {Array.from({ length: rows }, (_, r) => (
        <div key={r} style={{ display: 'grid', gridTemplateColumns: `repeat(${columns}, 1fr)`, gap: 16 }}>
          {Array.from({ length: columns }, (_, c) => (
            <Skeleton key={c} width={`${60 + ((r * 7 + c * 13) % 35)}%`} />
          ))}
        </div>
      ))}
    </div>
  );
}

export function SkeletonCards({ count = 6, height = 120 }: { count?: number; height?: number }) {
  return (
    <div className="sr-grid sr-grid--3" aria-busy aria-label="Yükleniyor">
      {Array.from({ length: count }, (_, i) => (
        <div key={i} className="sr-card">
          <Skeleton width="40%" height={16} />
          <Skeleton width="70%" height={24} style={{ marginTop: 12 }} />
          <Skeleton height={height - 70} radius={12} style={{ marginTop: 12 }} />
        </div>
      ))}
    </div>
  );
}

export interface DataStateProps {
  isLoading: boolean;
  error?: unknown;
  isEmpty?: boolean;
  forbidden?: boolean;
  onRetry?: () => void;
  empty?: ReactNode;
  skeleton?: ReactNode;
  /** zaman damgası: çevrimdışıyken son veri gösterilir */
  updatedAt?: number;
  children: ReactNode;
}

/**
 * Her veri ekranı: varsayılan, yükleniyor (skeleton), boş, hata (tekrar dene), izin yok, çevrimdışı (son veri + zaman).
 */
export function DataState({
  isLoading,
  error,
  isEmpty,
  forbidden,
  onRetry,
  empty,
  skeleton,
  updatedAt,
  children,
}: DataStateProps) {
  const online = useOnline();
  const status = (error as { status?: number } | undefined)?.status;
  if (forbidden || status === 403) return <ForbiddenState card />;
  if (isLoading) return <>{skeleton ?? <SkeletonRows />}</>;
  if (error && !online && updatedAt) {
    return (
      <div className="sr-stack" style={{ gap: 12 }}>
        <OfflineBadge lastSync={updatedAt} />
        {children}
      </div>
    );
  }
  if (error && !online) {
    return (
      <EmptyState
        card
        icon="wifi-off"
        tone="warn"
        title={copy.states.offline}
        body={copy.states.offlineBody}
        action={
          onRetry ? (
            <Button size="sm" variant="secondary" icon="refresh" onClick={onRetry}>
              {copy.actions.retry}
            </Button>
          ) : null
        }
      />
    );
  }
  if (error) return <ErrorState card onRetry={onRetry} />;
  if (isEmpty) return <>{empty ?? <EmptyState card icon="search" title={copy.states.empty} />}</>;
  return (
    <>
      {!online && updatedAt ? <OfflineBadge lastSync={updatedAt} /> : null}
      {children}
    </>
  );
}
